import React, { useEffect, useMemo } from 'react'
import {
  useModalContext,
  Button,
  get,
  Label,
  TextBody,
  Space,
  Box,
  upToMd,
  RadioButton,
  TextHeading,
  ButtonGroup,
  Alert,
} from '@ally/metronome-ui'
import styled from 'styled-components'
import { Form, RadioGroup, useFormsContext } from '@ally/forms'
import { Private } from '@ally/private'
import { Beneficiary, BeneficiaryClassificationCodes } from '../../../types'
import {
  BENEFICIARY_CLASSIFICATION_CODES,
  CLASSIFICATION_CODES,
} from '../../../utils/constants'
import { BeneficiaryDobField } from '../../common/BeneficiaryDobField'
import { BeneficiaryAddress } from '../../common/BeneficiaryAddress'
import { EditBeneIdentificationField } from './EditFormFields/EditBeneIdentificationField'
import { FundsAllocationFields } from '../../common/FundsAllocationFields'
import { maskSSN, formatDOBforInput } from '../../../utils/formatter'
import { fireErrorTagging, KeyValuePairObj } from '../../../utils/tagging'
import { BeneficiarySsnTaxId } from '../../common/BeneficiarySsnTaxId'

const headerRef = React.createRef<HTMLHeadingElement>()

const StyledAlert = styled(Alert)`
  margin-bottom: ${get.space('md')};
`

const StyledButtonGroup = styled(ButtonGroup)`
  margin-top: ${get.space('md')};
  margin-bottom: ${get.space('md')};
`

export const EditBeneficiaryForm: React.FC<{
  beneficiaryId: string
  beneficiaries: Beneficiary[] | undefined
  isLoading: boolean
  showErrorMessage: string
  isIraPage?: boolean
  stepForward: () => void
}> = ({
  beneficiaryId,
  beneficiaries,
  isLoading,
  showErrorMessage,
  isIraPage,
  stepForward,
}) => {
  const { setOpen } = useModalContext()
  const {
    formId,
    submitForm,
    setFieldValue,
    values,
    dirty,
    validateForm,
    errors,
    isSubmitting,
    setFieldError,
    setFieldTouched,
  } = useFormsContext()

  useEffect(() => {
    if (showErrorMessage.includes('ZIP code')) {
      setFieldError('zipCode', 'Enter correct ZIP code.')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showErrorMessage])

  const beneficiaryTaxIdLabel = useMemo(() => {
    return values.identificationNumber.charAt(2) === '-'
      ? 'Tax ID'
      : 'Social Security Number'
  }, [values.identificationNumber])

  // istanbul ignore next - chaining option
  const currentBeneficiary = beneficiaries?.find(
    bene => bene.beneficiaryId === beneficiaryId,
  )
  // istanbul ignore next - chaining option
  const arrayOfBeneIds = beneficiaries?.map(bene => bene.beneficiaryId)

  // istanbul ignore next - chaining option
  const total = arrayOfBeneIds
    ? arrayOfBeneIds.reduce((acc, curr) => {
        if (Number(values[curr])) {
          return acc + Number(values[curr])
        }
        return acc
      }, 0)
    : 0

  useEffect(() => {
    if (!values.maskedSsn && currentBeneficiary && !currentBeneficiary.taxId) {
      setFieldTouched('maskedSsn', true)
      setFieldTouched('maskedTaxId', true)
      setFieldTouched('beneIdentification', true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // istanbul ignore next - low value
  if (!currentBeneficiary || !beneficiaries) return <></>
  const {
    beneficiaryClassificationCode,
    beneficiaryName,
    lastName,
    firstName,
    middleName,
    relationshipToOwner,
    beneficiaryType,
    ssn,
    dateOfBirth,
    address: { country },
  } = currentBeneficiary

  const isOutsideTheUS = country !== 'US'

  const filteredIraBenes = beneficiaries.filter(
    bene => bene.beneficiaryType === beneficiaryType,
  )

  const filteredIds =
    filteredIraBenes && filteredIraBenes.map(bene => bene.beneficiaryId)

  const filteredValues = Object.keys(values).filter(
    id =>
      (filteredIds && filteredIds.includes(id)) ||
      id === 'newBeneficiaryFundAllocation',
  )
  const totalIraBenes = filteredValues.reduce((acc, curr) => {
    return acc + Number(values[curr])
  }, 0)

  // istanbul ignore next - low value
  const onDateChange = ({
    target: { value },
  }: React.BaseSyntheticEvent): void => {
    // we don't want to allow any digit other than numbers
    const numbersOnly = /[^0-9 //]/g
    if (numbersOnly.test(value)) {
      setFieldValue('dateOfBirth', value.replace(/[^0-9 //]/g, ''))
      // we are adding a backslash automatically as you type
    } else if (value.length === 2 || value.length === 5) {
      const newText = `${value}/`
      setFieldValue('dateOfBirth', newText)
    } else if (value.length <= 10) {
      setFieldValue('dateOfBirth', value)
    }
  }

  // istanbul ignore next
  const checkBackspace = (e: React.KeyboardEvent): void => {
    if (
      (e.key === 'Backspace' &&
        values.dateOfBirth[values.dateOfBirth.length - 1] === '/') ||
      e.key === '/'
    ) {
      const newText = values.dateOfBirth.slice(0, -1)
      setFieldValue('dateOfBirth', newText)
    }
  }

  // istanbul ignore next // optional chaining
  const isEqualDistribution = beneficiaries?.find(
    bene => bene.allocationPercentage === 'Equal',
  )

  const handleZipCodeChange = (value: string): void => {
    setFieldValue('zipCode', value)
  }

  const handleStateChange = (value: string): void => {
    setFieldValue('state', value)
  }

  const handleInputChange = (name: string, value: string): void => {
    setFieldValue(name, value)
  }

  // istanbul ignore next - low value
  const onBeneIdentificationChange = ({
    target: { value },
  }: React.BaseSyntheticEvent): void => {
    if (values.maskedSsn || values.maskedTaxId) {
      setFieldValue('maskedSsn', undefined, false)
      setFieldValue('maskedTaxId', undefined, false)
    }
    setFieldValue('beneIdentification', value)
  }

  const beneficiaryIdentification = (): React.ReactElement => {
    if (isIraPage) {
      return (
        <Box mt="md" mb="sm">
          <Label
            htmlFor="beneficiaryIdentificationNumber"
            data-testid="ira-social-security-label"
          >
            Social Security Number
          </Label>
          <Private>
            <TextBody tag="span" size="sm">
              {maskSSN(ssn as string)}
            </TextBody>
          </Private>
        </Box>
      )
    }
    if (beneficiaryClassificationCode === 'I') {
      return (
        <EditBeneIdentificationField
          beneficiaries={beneficiaries}
          beneficiaryId={beneficiaryId}
        />
      )
    }
    return (
      <Box mt="md" mb="sm">
        {!currentBeneficiary.taxId ? (
          // input field here, user has no SSN/TaxId and must enter one
          <BeneficiarySsnTaxId
            beneficiaryClassification={
              Object.keys(BENEFICIARY_CLASSIFICATION_CODES).find(
                key =>
                  BENEFICIARY_CLASSIFICATION_CODES[key] ===
                  beneficiaryClassificationCode,
              ) || /* istanbul ignore next - constants */ ''
            }
            changeBeneIdentificationFieldValue={setFieldValue}
            onBeneIdentificationChange={onBeneIdentificationChange}
            maskedSsn={values.maskedSsn}
            maskedTaxId={values.maskedTaxId}
            identificationNumber={values.identificationNumber}
            beneIdentification={values.beneIdentification}
            ssnEditDisabled={false}
            isIraPage={isIraPage}
          />
        ) : (
          <>
            <Label htmlFor="beneficiaryIdentificationNumber">
              {beneficiaryClassificationCode === 'T'
                ? beneficiaryTaxIdLabel
                : 'Tax ID'}
            </Label>
            <Private>
              <TextBody tag="span" size="sm">
                {values.maskedSsn}
              </TextBody>
            </Private>
          </>
        )}
      </Box>
    )
  }

  const fundsReallocation = (): React.ReactElement => {
    if (isIraPage) {
      return (
        <>
          {filteredIraBenes && filteredIraBenes.length > 1 ? (
            <>
              <Box width={upToMd('88%', '88%')}>
                <Label htmlFor="fundAllocationType">
                  How do you want to reallocate your money?
                </Label>
              </Box>
              <Box mb="md">
                <FundsAllocationFields
                  existingBeneficiaries={filteredIraBenes}
                  total={totalIraBenes}
                  hideNewBeneAllocationInput
                  setPercentageValue={handleInputChange}
                />
              </Box>
            </>
          ) : (
            <Box mt="md" mb="md">
              <Label
                htmlFor="allocationAmount"
                data-testid="allocation-amount-ira"
              >
                Allocation amount
              </Label>
              <TextBody tag="span" size="sm">
                100%
              </TextBody>
            </Box>
          )}
        </>
      )
    }
    return (
      <>
        {beneficiaries && beneficiaries.length > 1 ? (
          <>
            <Box width={upToMd('88%', '88%')}>
              <Label htmlFor="fundAllocationType">
                How do you want to reallocate your money?
              </Label>
              <RadioGroup
                name="fundAllocationType"
                legendText=""
                isLegendHidden
                defaultValue={
                  isEqualDistribution
                    ? 'equal'
                    : // istanbul ignore next // we have a test for this already
                      'specific'
                }
              >
                <RadioButton
                  label="Equally"
                  value="equal"
                  data-testid="equal-allocation"
                />
                <RadioButton
                  label="Specific percentage"
                  value="specific"
                  data-testid="specific-allocation"
                />
              </RadioGroup>
            </Box>
            <Box mb="md">
              {values.fundAllocationType === 'specific' ? (
                <FundsAllocationFields
                  existingBeneficiaries={beneficiaries}
                  total={total}
                  hideNewBeneAllocationInput
                  setPercentageValue={handleInputChange}
                />
              ) : (
                <Box mt="md" mb="md">
                  <Label htmlFor="allocationAmount">Allocation amount</Label>
                  <TextBody tag="span" size="sm">
                    Equal
                  </TextBody>
                </Box>
              )}
            </Box>
          </>
        ) : (
          <Box mt="md" mb="md">
            <Label htmlFor="allocationAmount">Allocation amount</Label>
            <TextBody tag="span" size="sm">
              100%
            </TextBody>
          </Box>
        )}
      </>
    )
  }

  const handleSubmit = async (): Promise<void> => {
    // istanbul ignore next - tagging
    if (isIraPage && totalIraBenes !== 100) {
      // istanbul ignore next - tagging
      fireErrorTagging({
        beneficiaryAllocation: 'Your total allocation must add up to 100%',
      } as KeyValuePairObj)
    }
    // istanbul ignore next - tagging
    if (
      !isIraPage &&
      values.fundAllocationType === 'specific' &&
      total !== 100
    ) {
      fireErrorTagging({
        beneficiaryAllocation: 'Your total allocation must add up to 100%',
      } as KeyValuePairObj)
    }
    if (
      // istanbul ignore next - tagging
      (isIraPage && totalIraBenes === 100) ||
      (!isIraPage &&
        values.fundAllocationType === 'specific' &&
        total === 100) ||
      (!isIraPage && values.fundAllocationType === 'equal')
    ) {
      fireErrorTagging(errors as KeyValuePairObj)
      if (isIraPage) {
        const validation = await validateForm()
        const errorFields = Object.keys(validation)
        if (errorFields.length === 0) {
          stepForward()
        }
      } else {
        submitForm()
      }
    }
  }

  return (
    <Form id={formId}>
      <Space mb="md">
        <TextHeading
          tag="h1"
          size="sm"
          weight="bold"
          ref={headerRef}
          tabIndex={-1}
          data-testid="editBeneModalHeader"
          id="add-bene-heading"
        >
          Edit beneficiary
        </TextHeading>
        <>
          {showErrorMessage && (
            <StyledAlert
              variant="error"
              focusOnMount
              disableContainer
              body={showErrorMessage}
            />
          )}
        </>
        <>
          {beneficiaryName ? (
            <Box mt="md" mb="sm">
              <Label htmlFor="beneficiaryName">Beneficiary name</Label>
              <Private>
                <TextBody tag="span" size="sm">
                  {beneficiaryName}
                </TextBody>
              </Private>
            </Box>
          ) : (
            <>
              <Box mt="md" mb="sm">
                <Label htmlFor="beneficiaryName">First name</Label>
                <Private>
                  <TextBody tag="span" size="sm">
                    {firstName}
                  </TextBody>
                </Private>
              </Box>
              {middleName && (
                <Box mt="md" mb="sm">
                  <Label htmlFor="beneficiaryName">M.I. (Optional)</Label>
                  <Private>
                    <TextBody tag="span" size="sm">
                      {middleName}
                    </TextBody>
                  </Private>
                </Box>
              )}
              <Box mt="md" mb="sm">
                <Label htmlFor="beneficiaryName">Last name</Label>
                <Private>
                  <TextBody tag="span" size="sm">
                    {lastName}
                  </TextBody>
                </Private>
              </Box>
            </>
          )}
        </>
        {!isIraPage && (
          <Box mb="sm">
            <Label htmlFor="beneficiaryName">Beneficiary classification</Label>
            <Private>
              <TextBody tag="span" size="sm">
                {
                  CLASSIFICATION_CODES[
                    beneficiaryClassificationCode as BeneficiaryClassificationCodes
                  ]
                }
              </TextBody>
            </Private>
          </Box>
        )}

        {beneficiaryClassificationCode === 'I' && (
          <BeneficiaryDobField
            onDateChange={onDateChange}
            checkBackspace={checkBackspace}
          />
        )}
        {isIraPage && (
          <Box mb="sm">
            <Label htmlFor="dateOfBirth">Date of Birth</Label>
            <Private>
              <TextBody tag="span" size="sm">
                {dateOfBirth ? formatDOBforInput(dateOfBirth) : '-/-'}
              </TextBody>
            </Private>
          </Box>
        )}
        {isIraPage && (
          <Box mb="sm">
            <Label htmlFor="relationshipToAccountOwner">
              Relationship to Account Owner
            </Label>
            <Private>
              <TextBody tag="span" size="sm">
                {relationshipToOwner}
              </TextBody>
            </Private>
          </Box>
        )}

        {beneficiaryIdentification()}

        <BeneficiaryAddress
          handleZipCodeChange={handleZipCodeChange}
          handleInputChange={handleInputChange}
          handleStateChange={handleStateChange}
          isOutsideTheUS={isOutsideTheUS}
        />
        {isIraPage && (
          <Box mb="sm">
            <Label htmlFor="relationshipToAccountOwner">Beneficiary Type</Label>
            <Private>
              <TextBody tag="span" size="sm">
                {beneficiaryType}
              </TextBody>
            </Private>
          </Box>
        )}

        {fundsReallocation()}
      </Space>
      <StyledButtonGroup>
        <Button
          allytmln="Submit Edit Beneficiary"
          text="Submit"
          onClick={handleSubmit}
          loading={isLoading}
          data-testid="submit-edit-button"
          disabled={!dirty || isSubmitting}
        />
        <Button
          allytmln="Cancel Edit Beneficiary"
          text="Cancel"
          onClick={(): void => setOpen(false)}
          variant="link"
          data-testid="cancel-edit-button"
        />
      </StyledButtonGroup>
    </Form>
  )
}
